<template>
  <v-row>
    <v-col cols="12">
      <base-card class="pa-2 mb-4">
        <v-card-title class="pa-0 pb-1 production-presets-title">
          <div>
            <div class="card-title mb-0">
              Production Layer Presets
            </div>

            <div class="production-presets-subtitle">
              {{ selectedProductionPresetTypeLabel }}
              · {{ productionLayerPresetRows.length }} layers
            </div>
          </div>

          <v-spacer />

          <v-btn
            text
            color="primary"
            @click="addProductionPresetLayer"
          >
            <v-icon left>
              mdi-plus
            </v-icon>

            Add Layer
          </v-btn>
        </v-card-title>

        <v-radio-group
          v-model="selectedProductionPresetType"
          row
          dense
          hide-details
          class="production-preset-types mt-1 mb-3"
        >
          <v-radio
            v-for="type in productionPresetTypes"
            :key="type.value"
            :label="type.text"
            :value="type.value"
            color="primary"
          />
        </v-radio-group>

        <v-progress-linear
          v-if="productionPresetLoading"
          indeterminate
          color="primary"
          class="mt-2"
        />

        <v-card-text
          v-else
          class="pa-0 pt-2"
        >
          <div class="production-presets-table">
            <div class="production-preset-grid production-preset-header">
              <div class="production-preset-number">
                #
              </div>

              <div />

              <div>
                Layer
              </div>

              <div>
                Type
              </div>

              <div>
                Label for user
              </div>

              <div>
                Mode
              </div>

              <div>
                Backend Dynamic Field
              </div>

              <div />
            </div>

            <draggable
              :list="productionLayerPresetRows"
              handle=".production-preset-drag"
              ghost-class="production-preset-ghost"
              chosen-class="production-preset-chosen"
            >
              <transition-group
                type="transition"
                name="production-preset-list"
              >
                <div
                  v-for="(row, index) in productionLayerPresetRows"
                  :key="row._key"
                  class="production-preset-grid production-preset-row"
                >
                  <div class="production-preset-number">
                    {{ index + 1 }}
                  </div>

                  <div class="production-preset-icon-cell">
                    <v-icon
                      small
                      class="production-preset-drag"
                    >
                      mdi-cursor-move
                    </v-icon>
                  </div>

                  <div>
                    <input
                      v-model.trim="row.layer"
                      class="prop-input production-preset-input"
                      :class="{
                        'production-preset-error':
                          !row.layer || isDuplicatePresetLayer(row, index),
                      }"
                      placeholder="NP.DYN.TXT.HEAD"
                    >

                    <div
                      v-if="isDuplicatePresetLayer(row, index)"
                      class="production-preset-error-text"
                    >
                      Duplicate layer
                    </div>
                  </div>

                  <div>
                    <select
                      v-model="row.type"
                      class="form-select production-preset-select"
                    >
                      <option
                        v-for="type in productionLayerTypeOptions"
                        :key="type"
                        :value="type"
                      >
                        {{ type }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <input
                      v-model.trim="row.label"
                      class="prop-input production-preset-input"
                      placeholder="Label visible for user"
                    >
                  </div>

                  <div class="production-preset-radio-line">
                    <label>
                      <input
                        v-model="row.mode"
                        type="radio"
                        value="auto_manual"
                        @change="productionPresetModeChanged(row)"
                      >

                      Auto / Manual
                    </label>

                    <label>
                      <input
                        v-model="row.mode"
                        type="radio"
                        value="only_auto"
                        @change="productionPresetModeChanged(row)"
                      >

                      Only Auto
                    </label>

                    <label>
                      <input
                        v-model="row.mode"
                        type="radio"
                        value="only_manual"
                        @change="productionPresetModeChanged(row)"
                      >

                      Only Manual
                    </label>
                  </div>

                  <div>
                    <select
                      v-if="productionPresetHasAutoMode(row)"
                      v-model="row.auto_source"
                      class="form-select production-preset-select"
                    >
                      <option value="">
                        Select
                      </option>

                      <option
                        v-for="option in backendDynamicFieldOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.text }}
                      </option>
                    </select>

                    <span
                      v-else
                      class="production-preset-manual"
                    >
                      manual only
                    </span>
                  </div>

                  <div class="production-preset-icon-cell">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="error"
                          v-bind="attrs"
                          v-on="on"
                          @click="removeProductionPresetLayer(index)"
                        >
                          <v-icon small>
                            mdi-trash-can-outline
                          </v-icon>
                        </v-btn>
                      </template>

                      <span>
                        Delete layer
                      </span>
                    </v-tooltip>
                  </div>
                </div>
              </transition-group>
            </draggable>

            <div
              v-if="productionLayerPresetRows.length"
              ref="productionPresetTableEnd"
              class="production-preset-scroll-anchor"
            />

            <div
              v-if="!productionLayerPresetRows.length"
              class="production-presets-empty"
            >
              <v-icon
                size="32"
                color="grey"
              >
                mdi-table-off
              </v-icon>

              <div class="mt-2">
                No production layer presets for
                {{ selectedProductionPresetTypeLabel }}
              </div>

              <v-btn
                text
                color="primary"
                class="mt-1"
                @click="addProductionPresetLayer"
              >
                <v-icon left>
                  mdi-plus
                </v-icon>

                Add Layer
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-card-actions v-if="!productionPresetLoading">
          <v-spacer />

          <v-btn
            color="success"
            :loading="productionPresetSaving"
            :disabled="!productionPresetId"
            @click="updateProductionLayerPresets"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>

            Update {{ selectedProductionPresetTypeLabel }} Preset
          </v-btn>
        </v-card-actions>
      </base-card>

      <base-card class="pa-2">
        <v-card-title class="pa-0 pb-2">
          <div>
            <div class="card-title mb-0">
              Job Templates
            </div>

            <div class="job-templates-subtitle">
              Nexrender job skeletons
            </div>
          </div>

          <v-spacer />

          <v-btn
            text
            color="primary"
            @click="newTemplate"
          >
            <v-icon left>
              mdi-plus
            </v-icon>

            Add Template
          </v-btn>
        </v-card-title>

        <v-progress-linear
          v-if="jobTemplateLoading"
          indeterminate
          color="primary"
          class="mt-2"
        />

        <div
          v-else
          class="job-template-panel"
        >
          <div class="job-template-list-row">
            <button
              v-for="template in orderedJobTemplates"
              :key="template.id"
              type="button"
              class="job-template-item"
              :class="{
                'job-template-selected':
                  selectedTemplateId === template.id,
              }"
              @click="templateChanged(template)"
            >
              <span class="job-template-item-id">
                {{ template.id }}
              </span>

              <span class="job-template-item-separator">
                -
              </span>

              <span class="job-template-item-name">
                {{ template.name }}
              </span>

              <span
                v-if="template.is_default"
                class="job-template-item-default"
              >
                Default
              </span>
            </button>
          </div>

          <div class="job-template-edit-row">
            <v-text-field
              v-model.trim="selectedTemplate.name"
              label="Name"
              dense
              hide-details
              outlined
              class="job-template-name-field"
            />

            <div
              v-if="selectedTemplateIsDefault"
              class="job-template-default-note"
            >
              Default template — editable, cannot be deleted.
            </div>
          </div>
        </div>
      </base-card>

      <div class="mt-4">
        <div ref="editor" />
      </div>

      <div class="d-flex justify-end mt-3">
        <v-btn
          v-if="
            selectedTemplateId !== 'new' &&
              selectedTemplateId &&
              !selectedTemplateIsDefault
          "
          color="error"
          class="mr-2"
          :disabled="jobTemplateSaving || jobTemplateDeleting"
          @click="removeDialog = true"
        >
          <v-icon class="pr-1">
            mdi-delete-outline
          </v-icon>

          Delete
        </v-btn>

        <v-btn
          v-if="
            selectedTemplateId !== 'new' &&
              selectedTemplateId
          "
          color="primary"
          :loading="jobTemplateSaving"
          :disabled="jobTemplateDeleting"
          @click="updateTemplate"
        >
          <v-icon class="pr-1">
            mdi-content-save
          </v-icon>

          Save
        </v-btn>

        <v-btn
          v-if="selectedTemplateId === 'new'"
          color="success"
          :loading="jobTemplateSaving"
          @click="addTemplate"
        >
          <v-icon class="pr-1">
            mdi-content-save
          </v-icon>

          Create
        </v-btn>
      </div>
    </v-col>

    <v-dialog
      v-model="removeDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          Delete job template?
        </v-card-title>

        <v-card-text>
          <div class="row p-b-20">
            <div class="col-md-12">
              Are you sure you want to delete the job template
              "{{ selectedTemplate.name }}"?
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            depressed
            text
            @click="removeDialog = false"
          >
            No
          </v-btn>

          <v-btn
            depressed
            color="error"
            :loading="jobTemplateDeleting"
            :disabled="selectedTemplateIsDefault"
            @click="deleteTemplate"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
  import { processAEPProject } from '@/store/converters'
  import { basicSetup, EditorView } from 'codemirror'
  import { json, jsonParseLinter } from '@codemirror/lang-json'
  import { linter } from '@codemirror/lint'
  import draggable from 'vuedraggable'

  const linterExtension = linter(jsonParseLinter())

  const defaultJobTemplate = {
    template: {
      src: '',
      composition: '',
      outputModule:
        'H.264 - Match Render Settings - 15 Mbps',
      outputExt: 'mp4',
      settingsTemplate: 'Best Settings',
      continueOnMissing: false,
    },

    assets: [],

    actions: {
      postrender: [
        {
          module: '@nexrender/action-upload',
          provider: 'ftp',

          params: {
            host: 'storage007.noisypeak.com',
            port: 21,
            user: 'template007',
            password: 'YfnP14iRa',
            output: '/',
          },
        },
      ],
    },
  }

  export default {
    name: 'Settings',

    components: {
      draggable,
    },

    metaInfo: {
      title: 'Settings',
    },

    data () {
      return {
        productionPresetId: null,
        productionPresetLoading: false,
        productionPresetSaving: false,

        selectedProductionPresetType: 'intro',

        productionPresetLayersByType: {},
        productionPresetStoredLayersByType: {},

        productionPresetTypes: [
          {
            value: 'intro',
            text: 'Intro / Ident',
          },
          {
            value: 'announce',
            text: 'What\'s Next Announce',
          },
          {
            value: 'bumper',
            text: 'Bumper',
          },
          {
            value: 'overlay',
            text: 'Overlay',
          },
          {
            value: 'blackout',
            text: 'Blackout',
          },
          {
            value: 'ad-placeholder',
            text: 'Ad Placeholder',
          },
          {
            value: 'logo-reveal',
            text: 'Logo Reveal',
          },
          {
            value: 'live-start',
            text: 'Live Program Starting',
          },
          {
            value: 'live-end',
            text: 'Live Program Ended',
          },
          {
            value: 'channel-pitch',
            text: 'Presentation / Pitch',
          },
        ],

        productionLayerTypeOptions: [
          'Text',
          'Image',
          'Video',
          'Media',
          'Audio',
        ],

        backendDynamicFieldOptions: [
          {
            text: 'Program 1 — Title',
            value: 'prog1name',
          },
          {
            text: 'Program 1 — Start Time',
            value: 'prog1time',
          },
          {
            text: 'Program 1 — Image',
            value: 'prog1img',
          },
          {
            text: 'Program 1 — Description',
            value: 'prog1descr',
          },
          {
            text: 'Program 1 — Video',
            value: 'prog1video',
          },

          {
            text: 'Program 2 — Title',
            value: 'prog2name',
          },
          {
            text: 'Program 2 — Start Time',
            value: 'prog2time',
          },
          {
            text: 'Program 2 — Image',
            value: 'prog2img',
          },
          {
            text: 'Program 2 — Description',
            value: 'prog2descr',
          },
          {
            text: 'Program 2 — Video',
            value: 'prog2video',
          },

          {
            text: 'Program 3 — Title',
            value: 'prog3name',
          },
          {
            text: 'Program 3 — Start Time',
            value: 'prog3time',
          },
          {
            text: 'Program 3 — Image',
            value: 'prog3img',
          },
          {
            text: 'Program 3 — Description',
            value: 'prog3descr',
          },
          {
            text: 'Program 3 — Video',
            value: 'prog3video',
          },

          {
            text: 'Channel — Logo',
            value: 'channelLogo',
          },
          {
            text: 'Channel — Short Description',
            value: 'channelTagline',
          },
          {
            text: 'Channel — Copyright',
            value: 'channelCopyright',
          },
          {
            text: 'Channel — Contact Information',
            value: 'channelContact',
          },
          {
            text: 'Channel — Primary Color',
            value: 'channelPrimaryColor',
          },
          {
            text: 'Channel — Secondary Color',
            value: 'channelSecondaryColor',
          },
          {
            text: 'Channel — Heading Font',
            value: 'channelHeadingFont',
          },
          {
            text: 'Channel — Body Font',
            value: 'channelBodyFont',
          },
          {
            text: 'Channel — Music',
            value: 'channelMusic',
          },
          {
            text: 'NP Logo — 1',
            value: 'npLogo1',
          },
          {
            text: 'NP Logo — 0',
            value: 'npLogo0',
          },
          {
            text: 'Rating — Badge Image',
            value: 'ratingImage',
          },
          {
            text: 'Rating — Label',
            value: 'ratingText',
          },
          {
            text: 'Rating — Advisories',
            value: 'ratingAdv',
          },

          {
            text: 'Channel Social — Website',
            value: 'channelSocialWebsite',
          },
          {
            text: 'Channel Social — Instagram',
            value: 'channelSocialInstagram',
          },
          {
            text: 'Channel Social — X / Twitter',
            value: 'channelSocialTwitter',
          },
          {
            text: 'Channel Social — Facebook',
            value: 'channelSocialFacebook',
          },
          {
            text: 'Channel Social — Snapchat',
            value: 'channelSocialSnapchat',
          },
          {
            text: 'Channel Social — YouTube',
            value: 'channelSocialYoutube',
          },
          {
            text: 'Channel Social — Telegram',
            value: 'channelSocialTelegram',
          },
          {
            text: 'Channel Social — Tumblr',
            value: 'channelSocialTumblr',
          },
          {
            text: 'Channel Social — Discord',
            value: 'channelSocialDiscord',
          },
          {
            text: 'Channel Social — BeReal',
            value: 'channelSocialBereal',
          },
          {
            text: 'Channel Social — Spotify',
            value: 'channelSocialSpotify',
          },
        ],

        selectedTemplateId: null,

        templates: [],
        selectedTemplate: {
          id: null,
          name: '',
          is_default: false,
          template: {},
        },

        jobTemplateLoading: false,
        jobTemplateSaving: false,
        jobTemplateDeleting: false,

        search: '',
        projects: [],
        aepFile: null,
        fileLoading: false,
        fileParsing: false,
        polling: null,
        removeDialog: false,
        removedObj: {},
      }
    },

    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },

      orderedJobTemplates () {
        return this.templates
          .slice()
          .sort((a, b) => {
            if (a.is_default && !b.is_default) {
              return -1
            }

            if (!a.is_default && b.is_default) {
              return 1
            }

            return a.id - b.id
          })
      },

      selectedTemplateIsDefault () {
        return !!(
          this.selectedTemplate &&
          this.selectedTemplate.is_default
        )
      },

      productionLayerPresetRows () {
        return (
          this.productionPresetLayersByType[
            this.selectedProductionPresetType
          ] || []
        )
      },

      selectedProductionPresetTypeLabel () {
        const type = this.productionPresetTypes.find(
          (item) =>
            item.value ===
            this.selectedProductionPresetType,
        )

        return type
          ? type.text
          : this.selectedProductionPresetType
      },
    },

    mounted () {
      this.content = JSON.stringify(
        defaultJobTemplate,
        null,
        ' ',
      )

      this.view = new EditorView({
        doc: this.content,

        extensions: [
          basicSetup,
          json(),
          linterExtension,
        ],

        parent: this.$refs.editor,
      })

      this.loadJobTemplates()
    },

    created () {
      this.initializeProductionPresetTypes()
      this.loadProductionLayerPresets()
      this.loadData()
    },

    beforeDestroy () {
      clearInterval(this.polling)

      if (this.view) {
        this.view.destroy()
      }
    },

    methods: {
      initializeProductionPresetTypes () {
        for (
          let i = 0;
          i < this.productionPresetTypes.length;
          i += 1
        ) {
          const type =
            this.productionPresetTypes[i].value

          if (
            !Array.isArray(
              this.productionPresetLayersByType[type],
            )
          ) {
            this.$set(
              this.productionPresetLayersByType,
              type,
              [],
            )
          }

          if (
            !Array.isArray(
              this.productionPresetStoredLayersByType[type],
            )
          ) {
            this.$set(
              this.productionPresetStoredLayersByType,
              type,
              [],
            )
          }
        }
      },

      getProductionPresetResultRows (result) {
        if (Array.isArray(result)) {
          return result
        }

        if (
          result &&
          Array.isArray(result.items)
        ) {
          return result.items
        }

        return []
      },

      setProductionPresetTypeLayers (
        type,
        layers,
      ) {
        const normalized =
          this.normalizeProductionPresetLayers(
            layers,
          )

        const stored =
          this.serializeProductionPresetLayers(
            normalized,
          )

        this.$set(
          this.productionPresetLayersByType,
          type,
          normalized,
        )

        this.$set(
          this.productionPresetStoredLayersByType,
          type,
          stored,
        )
      },

      loadProductionLayerPresets () {
        this.productionPresetLoading = true

        const params = {
          'id[eq]': 1,
        }

        this.$store.dispatch(
          'playoutSettings',
          params,
        )
          .then((result) => {
            const settingsRows =
              this.getProductionPresetResultRows(
                result,
              )

            if (!settingsRows.length) {
              this.productionPresetId = null

              this.initializeProductionPresetDefaults()

              this.$store.commit(
                'showSnackbar',
                {
                  text:
                    'Global playout settings record was not found',
                  timeout: 6000,
                  color: 'warning',
                },
              )

              return
            }

            const playoutSetting =
              settingsRows[0]

            const settings =
              this.parseProductionPresetSettings(
                playoutSetting.aep_layer_preset,
              )

            this.productionPresetId =
              playoutSetting.id

            this.applyLoadedProductionPresetSettings(
              settings,
            )
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.productionPresetLoading = false
          })
      },

      applyLoadedProductionPresetSettings (
        settings,
      ) {
        const typeSettings =
          settings &&
          settings.types &&
          typeof settings.types === 'object'
            ? settings.types
            : null

        if (typeSettings) {
          for (
            let i = 0;
            i < this.productionPresetTypes.length;
            i += 1
          ) {
            const type =
              this.productionPresetTypes[i].value

            const hasPreset =
              Object.prototype.hasOwnProperty.call(
                typeSettings,
                type,
              )

            if (
              type === 'overlay' &&
              !hasPreset
            ) {
              this.setProductionPresetTypeLayers(
                type,
                this.buildDefaultOverlayProductionPresetLayers(),
              )

              continue
            }

            const preset =
              typeSettings[type] || {}

            const layers =
              Array.isArray(preset)
                ? preset
                : preset.layers

            this.setProductionPresetTypeLayers(
              type,
              Array.isArray(layers)
                ? layers
                : [],
            )
          }

          return
        }

        const legacyLayers =
          Array.isArray(settings)
            ? settings
            : settings.layers

        if (Array.isArray(legacyLayers)) {
          for (
            let i = 0;
            i < this.productionPresetTypes.length;
            i += 1
          ) {
            const type =
              this.productionPresetTypes[i].value

            this.setProductionPresetTypeLayers(
              type,
              legacyLayers,
            )
          }

          return
        }

        this.initializeProductionPresetDefaults()
      },

      initializeProductionPresetDefaults () {
        const defaults =
          this.buildDefaultProductionPresetLayers()

        const serializedDefaults =
          this.serializeProductionPresetLayers(
            defaults,
          )

        for (
          let i = 0;
          i < this.productionPresetTypes.length;
          i += 1
        ) {
          const type =
            this.productionPresetTypes[i].value

          this.setProductionPresetTypeLayers(
            type,
            type === 'overlay'
              ? this.buildDefaultOverlayProductionPresetLayers()
              : serializedDefaults,
          )
        }
      },

      parseProductionPresetSettings (
        settings,
      ) {
        if (!settings) {
          return {
            version: 2,
            types: {},
          }
        }

        if (typeof settings === 'object') {
          return settings
        }

        try {
          return JSON.parse(settings)
        } catch (error) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Invalid Production Layer Presets JSON',
              timeout: 6000,
              color: 'error',
            },
          )

          return {
            version: 2,
            types: {},
          }
        }
      },

      normalizeProductionPresetLayers (
        layers,
      ) {
        if (!Array.isArray(layers)) {
          return []
        }

        return layers.map((row) => (
          this.createProductionPresetLayer({
            layer: row.layer || '',
            type: row.type || 'Text',
            label: row.label || '',
            mode:
              row.mode ||
              'auto_manual',

            auto_source:
              row.auto_source ||
              row.autoSource ||
              '',
          })
        ))
      },

      createProductionPresetLayer (
        values = {},
      ) {
        return {
          _key:
            `preset-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`,

          layer: values.layer || '',
          type: values.type || 'Text',
          label: values.label || '',

          mode:
            values.mode ||
            'auto_manual',

          auto_source:
            values.auto_source ||
            '',
        }
      },

      buildDefaultProductionPresetLayers () {
        return [
          this.createProductionPresetLayer({
            layer: 'NP.DYN.COL.PRI',
            type: 'Text',
            label: 'Primary color',
            mode: 'only_auto',
            auto_source: 'channelPrimaryColor',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.COL.SEC',
            type: 'Text',
            label: 'Secondary color',
            mode: 'only_auto',
            auto_source: 'channelSecondaryColor',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.FONT.HEADER',
            type: 'Text',
            label: 'Heading font',
            mode: 'auto_manual',
            auto_source: 'channelHeadingFont',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.FONT.NORMAL',
            type: 'Text',
            label: 'Body font',
            mode: 'auto_manual',
            auto_source: 'channelBodyFont',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.NP_LOGO.TXT',
            type: 'Text',
            label: 'Logo text',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.COPY.TXT',
            type: 'Text',
            label: 'Copyright',
            mode: 'only_auto',
            auto_source: 'channelCopyright',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.CONTACT.TXT',
            type: 'Text',
            label: 'Contact information',
            mode: 'auto_manual',
            auto_source: 'channelContact',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.AUD.MUSIC',
            type: 'Audio',
            label: 'Background music',
            mode: 'auto_manual',
            auto_source: 'channelMusic',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.VID.BKG',
            type: 'Video',
            label: 'Background video',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.IMG.LOGO',
            type: 'Image',
            label: 'Logo',
            mode: 'auto_manual',
            auto_source: 'channelLogo',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.TAGLINE',
            type: 'Text',
            label: 'Tagline',
            mode: 'auto_manual',
            auto_source: 'channelTagline',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.HEAD',
            type: 'Text',
            label: 'Headline',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.DESC',
            type: 'Text',
            label: 'Description',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.X',
            type: 'Text',
            label: 'X / Twitter',
            mode: 'auto_manual',
            auto_source: 'channelSocialTwitter',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.TW',
            type: 'Text',
            label: 'Twitter',
            mode: 'auto_manual',
            auto_source: 'channelSocialTwitter',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.LN',
            type: 'Text',
            label: 'LinkedIn',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.RE',
            type: 'Text',
            label: 'Reddit',
            mode: 'auto_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.IMG.QR',
            type: 'Image',
            label: 'QR code',
            mode: 'only_manual',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.FB',
            type: 'Text',
            label: 'Facebook',
            mode: 'auto_manual',
            auto_source: 'channelSocialFacebook',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.TT',
            type: 'Text',
            label: 'TikTok',
            mode: 'auto_manual',
          }),
        ]
      },

      buildDefaultOverlayProductionPresetLayers () {
        const layers =
          this.buildDefaultProductionPresetLayers()

        layers.push(
          this.createProductionPresetLayer({
            layer: 'NP.DYN.IMG.RATING1',
            type: 'Image',
            label: 'Rating badge',
            mode: 'auto_manual',
            auto_source: 'ratingImage',
          }),

          this.createProductionPresetLayer({
            layer: 'NP.DYN.TXT.RATING1',
            type: 'Text',
            label: 'Rating',
            mode: 'auto_manual',
            auto_source: 'ratingText',
          }),
        )

        for (let i = 1; i <= 6; i += 1) {
          layers.push(
            this.createProductionPresetLayer({
              layer: `NP.DYN.IMG.DESC${i}`,
              type: 'Image',
              label: `Descriptor icon ${i}`,
              mode: 'only_manual',
            }),

            this.createProductionPresetLayer({
              layer: `NP.DYN.TXT.DESC${i}`,
              type: 'Text',
              label: `Descriptor text ${i}`,
              mode: 'only_manual',
            }),
          )
        }

        for (let i = 1; i <= 4; i += 1) {
          layers.push(
            this.createProductionPresetLayer({
              layer: `NP.DYN.IMG.ADV${i}`,
              type: 'Image',
              label: `Advisory icon ${i}`,
              mode: 'only_manual',
            }),

            this.createProductionPresetLayer({
              layer: `NP.DYN.TXT.ADV${i}`,
              type: 'Text',
              label: `Advisory text ${i}`,
              mode: 'only_manual',
            }),
          )
        }

        return layers
      },

      addProductionPresetLayer () {
        this.productionLayerPresetRows.push(
          this.createProductionPresetLayer(),
        )

        this.$nextTick(() => {
          const target =
            this.$refs.productionPresetTableEnd

          if (
            target &&
            typeof target.scrollIntoView === 'function'
          ) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            })
          }
        })
      },

      removeProductionPresetLayer (
        index,
      ) {
        this.productionLayerPresetRows.splice(
          index,
          1,
        )
      },

      productionPresetHasAutoMode (
        row,
      ) {
        return (
          row.mode === 'auto_manual' ||
          row.mode === 'only_auto'
        )
      },

      productionPresetModeChanged (
        row,
      ) {
        if (row.mode === 'only_manual') {
          row.auto_source = ''
        }
      },

      normalizeProductionPresetLayerName (
        value,
      ) {
        return String(value || '')
          .trim()
          .toUpperCase()
      },

      isDuplicatePresetLayer (
        row,
        index,
      ) {
        const currentLayer =
          this.normalizeProductionPresetLayerName(
            row.layer,
          )

        if (!currentLayer) {
          return false
        }

        return this.productionLayerPresetRows.some(
          (item, itemIndex) => {
            if (itemIndex === index) {
              return false
            }

            return (
              this.normalizeProductionPresetLayerName(
                item.layer,
              ) === currentLayer
            )
          },
        )
      },

      validateProductionLayerPresets () {
        const names = {}

        for (
          let i = 0;
          i < this.productionLayerPresetRows.length;
          i += 1
        ) {
          const row =
            this.productionLayerPresetRows[i]

          const layer =
            this.normalizeProductionPresetLayerName(
              row.layer,
            )

          if (!layer) {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  `Layer name is required in row ${i + 1}`,
                timeout: 5000,
                color: 'error',
              },
            )

            return false
          }

          if (names[layer]) {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  `Duplicate production layer: ${row.layer}`,
                timeout: 5000,
                color: 'error',
              },
            )

            return false
          }

          names[layer] = true
        }

        return true
      },

      serializeProductionPresetLayers (
        rows,
      ) {
        if (!Array.isArray(rows)) {
          return []
        }

        return rows.map((row) => ({
          layer:
            String(row.layer || '').trim(),

          type:
            row.type ||
            'Text',

          label:
            String(row.label || '').trim(),

          mode:
            row.mode ||
            'auto_manual',

          auto_source:
            row.mode === 'only_manual'
              ? ''
              : row.auto_source || '',
        }))
      },

      serializeProductionPresetTypesForSave () {
        const types = {}

        for (
          let i = 0;
          i < this.productionPresetTypes.length;
          i += 1
        ) {
          const type =
            this.productionPresetTypes[i].value

          const rows =
            type === this.selectedProductionPresetType
              ? this.productionLayerPresetRows
              : (
                this.productionPresetStoredLayersByType[
                  type
                ] || []
              )

          types[type] = {
            layers:
              this.serializeProductionPresetLayers(
                rows,
              ),
          }
        }

        return types
      },

      updateProductionLayerPresets () {
        if (!this.productionPresetId) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Global playout settings record was not found',
              timeout: 6000,
              color: 'error',
            },
          )

          return
        }

        if (!this.validateProductionLayerPresets()) {
          return
        }

        this.productionPresetSaving = true

        const selectedType =
          this.selectedProductionPresetType

        const selectedLayers =
          this.serializeProductionPresetLayers(
            this.productionLayerPresetRows,
          )

        const payload = {
          id: this.productionPresetId,

          aep_layer_preset: {
            version: 2,

            types:
              this.serializeProductionPresetTypesForSave(),
          },
        }

        this.$store.dispatch(
          'patchPlayoutSetting',
          payload,
        )
          .then(() => {
            this.$set(
              this.productionPresetStoredLayersByType,
              selectedType,
              selectedLayers,
            )

            this.$store.commit(
              'showSnackbar',
              {
                text:
                  `${this.selectedProductionPresetTypeLabel} preset has been updated`,
                timeout: 3000,
                color: 'success',
              },
            )
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.productionPresetSaving = false
          })
      },

      cloneJobTemplate (
        template,
      ) {
        return JSON.parse(
          JSON.stringify(
            template ||
              defaultJobTemplate,
          ),
        )
      },

      getJobTemplateResultRows (
        result,
      ) {
        if (Array.isArray(result)) {
          return result
        }

        if (
          result &&
          Array.isArray(result.items)
        ) {
          return result.items
        }

        return []
      },

      parseJobTemplateValue (
        value,
      ) {
        if (!value) {
          return this.cloneJobTemplate(
            defaultJobTemplate,
          )
        }

        if (typeof value === 'object') {
          return this.cloneJobTemplate(value)
        }

        try {
          return JSON.parse(value)
        } catch (error) {
          return this.cloneJobTemplate(
            defaultJobTemplate,
          )
        }
      },

      normalizeJobTemplate (
        row,
      ) {
        return {
          id: row.id,
          name: row.name || '',
          is_default: !!(
            row.is_default ||
            row.isDefault
          ),

          template:
            this.parseJobTemplateValue(
              row.template,
            ),
        }
      },

      loadJobTemplates (
        preferredId = null,
      ) {
        this.jobTemplateLoading = true

        const params = {
          $orderby: 'id',
        }

        return this.$store.dispatch(
          'nexrenderJobTemplates',
          params,
        )
          .then((result) => {
            const rows =
              this.getJobTemplateResultRows(
                result,
              )

            this.templates =
              rows.map((row) =>
                this.normalizeJobTemplate(row),
              )

            if (!this.templates.length) {
              this.newTemplate()
              return
            }

            let selected = null

            if (preferredId) {
              selected =
                this.templates.find(
                  (item) =>
                    item.id === preferredId,
                )
            }

            if (!selected) {
              selected =
                this.templates.find(
                  (item) =>
                    item.is_default,
                ) ||
                this.templates[0]
            }

            this.templateChanged(selected)
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.jobTemplateLoading = false
          })
      },

      templateChanged (
        template,
      ) {
        if (!template) {
          return
        }

        this.selectedTemplateId =
          template.id

        this.selectedTemplate = {
          id: template.id,
          name: template.name,
          is_default:
            !!template.is_default,

          template:
            this.cloneJobTemplate(
              template.template,
            ),
        }

        this.jobChanged()
      },

      newTemplate () {
        const defaultTemplate =
          this.templates.find(
            (item) => item.is_default,
          )

        this.selectedTemplateId = 'new'

        this.selectedTemplate = {
          id: null,
          name: 'New Template',
          is_default: false,

          template:
            this.cloneJobTemplate(
              defaultTemplate
                ? defaultTemplate.template
                : defaultJobTemplate,
            ),
        }

        this.jobChanged()
      },

      getEditorJobTemplate () {
        try {
          return JSON.parse(
            this.view.state.doc.toString(),
          )
        } catch (error) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                `Invalid JSON: ${error}`,
              timeout: 6000,
              color: 'error',
            },
          )

          return null
        }
      },

      validateJobTemplateName () {
        const name =
          String(
            this.selectedTemplate.name ||
              '',
          ).trim()

        if (!name) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Template name is required',
              timeout: 5000,
              color: 'error',
            },
          )

          return ''
        }

        return name
      },

      addTemplate () {
        const name =
          this.validateJobTemplateName()

        if (!name) {
          return
        }

        const template =
          this.getEditorJobTemplate()

        if (!template) {
          return
        }

        this.jobTemplateSaving = true

        const payload = {
          name,
          template,
        }

        this.$store.dispatch(
          'createNexrenderJobTemplate',
          payload,
        )
          .then((result) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'Job template has been created',
                timeout: 3000,
                color: 'success',
              },
            )

            return this.loadJobTemplates(
              result && result.id
                ? result.id
                : null,
            )
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.jobTemplateSaving = false
          })
      },

      updateTemplate () {
        if (
          !this.selectedTemplateId ||
          this.selectedTemplateId === 'new'
        ) {
          return
        }

        const name =
          this.validateJobTemplateName()

        if (!name) {
          return
        }

        const template =
          this.getEditorJobTemplate()

        if (!template) {
          return
        }

        this.jobTemplateSaving = true

        const payload = {
          id: this.selectedTemplateId,
          name,
          template,
        }

        this.$store.dispatch(
          'patchNexrenderJobTemplate',
          payload,
        )
          .then(() => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'Job template has been updated',
                timeout: 3000,
                color: 'success',
              },
            )

            return this.loadJobTemplates(
              this.selectedTemplateId,
            )
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.jobTemplateSaving = false
          })
      },

      deleteTemplate () {
        if (
          !this.selectedTemplateId ||
          this.selectedTemplateId === 'new'
        ) {
          return
        }

        if (this.selectedTemplateIsDefault) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Default job template cannot be deleted',
              timeout: 5000,
              color: 'error',
            },
          )

          return
        }

        this.jobTemplateDeleting = true

        this.$store.dispatch(
          'deleteNexrenderJobTemplate',
          this.selectedTemplate,
        )
          .then(() => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'Job template has been deleted',
                timeout: 3000,
                color: 'success',
              },
            )

            this.removeDialog = false

            return this.loadJobTemplates()
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.jobTemplateDeleting = false
          })
      },

      jobChanged () {
        this.content = JSON.stringify(
          this.selectedTemplate.template,
          null,
          ' ',
        )

        if (!this.view) {
          return
        }

        const transaction =
          this.view.state.update({
            changes: {
              from: 0,
              to: this.view.state.doc.length,
              insert: this.content,
            },
          })

        this.view.dispatch(transaction)
      },

      loadData (silent) {
        if (!silent) {
          this.$store.commit('setPageLoader')
        }

        const params = {
          $orderby: '-id',
        }

        const aepReq =
          this.$store.dispatch(
            'aepProjects',
            params,
          )

        Promise.all([aepReq])
          .then((result) => {
            const projs = result[0] || []
            const items = []

            for (
              let i = 0;
              i < projs.length;
              i += 1
            ) {
              const item =
                processAEPProject(projs[i])

              item.duration =
                item.root.compDur.toFixed(2)

              item.assetsCount =
                item.props.length

              items.push(item)
            }

            this.projects = items
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text: errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.$store.commit(
              'resolvePageLoader',
            )
          })
      },

      openDeleteDialog (aep) {
        this.removeDialog = true
        this.removedObj = aep
      },

      deleteAEP () {
        if (!this.removedObj.id) {
          return
        }

        this.$store.commit('setPageLoader')

        this.$store.dispatch(
          'deleteAEPProject',
          this.removedObj,
        )
          .then(() => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'The AEP project has been removed',
                timeout: 6000,
                color: 'success',
              },
            )

            this.loadData()
            this.removedObj = {}
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text: errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.removeDialog = false

            this.$store.commit(
              'resolvePageLoader',
            )
          })
      },

      goToDetails (aep) {
        this.$router.push({
          name: 'TemplateDetails',

          params: {
            template_id: aep.id,
          },
        })
      },
    },
  }
</script>

<style lang="scss">
.cm-editor * {
  font-size: 13px !important;
}

.job-templates-subtitle {
  margin-top: 3px;
  font-size: 13px;
  line-height: 18px;
  color: #8a8f9c;
  font-weight: 400;
}

.job-template-panel {
  width: 100%;
}

.job-template-list-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
  background: #f7f9fc;
}

.job-template-item {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid #cfd5dd;
  border-radius: 4px;
  background: #fff;
  color: #2c3e50;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;
}

.job-template-item:hover {
  border-color: var(--v-primary-base);
}

.job-template-selected {
  border-color: var(--v-primary-base);
  background: #edf4fd;
}

.job-template-item-id {
  color: #7f8792;
  font-weight: 600;
}

.job-template-item-separator {
  color: #a0a6ae;
}

.job-template-item-name {
  font-weight: 500;
}

.job-template-item-default {
  margin-left: 4px;
  padding: 1px 5px;
  border-radius: 9px;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
}

.job-template-edit-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
}

.job-template-name-field {
  flex: 0 1 520px;
  max-width: 520px;
}

.job-template-default-note {
  color: #667080;
  font-size: 13px;
  line-height: 18px;
}

.production-presets-title {
  align-items: flex-start;
}

.production-presets-subtitle {
  margin-top: 3px;
  font-size: 13px;
  line-height: 18px;
  color: #8a8f9c;
  font-weight: 400;
}

.production-preset-types {
  padding: 7px 10px;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
  background: #f7f9fc;
}

.production-preset-types .v-input--radio-group__input {
  display: flex;
  flex-wrap: wrap;
  column-gap: 20px;
  row-gap: 4px;
}

.production-preset-types .v-radio {
  margin-right: 0 !important;
}

.production-preset-types .v-label {
  color: #2c3e50;
  font-size: 13px;
}

.production-presets-help {
  margin-bottom: 14px;
  padding: 9px 11px;
  border-left: 3px solid var(--v-primary-base);
  background: #f5f8fc;
  color: #667080;
  font-size: 13px;
  line-height: 18px;
}

.production-presets-table {
  width: 100%;
  min-width: 0;
  overflow: visible;
}

.production-preset-grid {
  width: 100%;
  display: grid;

  grid-template-columns:
    24px
    26px
    minmax(145px, 1.15fr)
    88px
    minmax(150px, 1.15fr)
    minmax(310px, 2.15fr)
    minmax(210px, 1.55fr)
    34px;

  column-gap: 8px;
  align-items: start;
}

.production-preset-grid > div {
  min-width: 0;
}

.production-preset-header {
  min-height: 35px;
  color: grey;
  font-size: 14px;
}

.production-preset-row {
  min-height: 37px;
  padding-bottom: 5px;
}

.production-preset-number {
  height: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa0aa;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
}

.production-preset-icon-cell {
  height: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.production-preset-drag {
  color: #8a8a8a;
  cursor: grab;
}

.production-preset-drag:active {
  cursor: grabbing;
}

.production-preset-input,
.production-preset-select {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  height: 31px;
  font-size: 13px;
  line-height: 20px;
}

.production-preset-radio-line {
  min-width: 0;
  height: 31px;
  display: flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
}

.production-preset-radio-line label {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: #2c3e50;
  font-size: 13px;
  cursor: pointer;
}

.production-preset-radio-line input {
  flex: 0 0 auto;
  margin: 0;
}

.production-preset-manual {
  height: 31px;
  display: flex;
  align-items: center;
  color: #8a8a8a;
  font-size: 13px;
}

.production-preset-error {
  border-color: #ff5252 !important;
  border-bottom-width: 2px !important;
}

.production-preset-error-text {
  margin-top: 2px;
  color: #ff5252;
  font-size: 11px;
  line-height: 13px;
}

.production-preset-scroll-anchor {
  width: 100%;
  height: 1px;
}

.production-presets-empty {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8a8a8a;
}

.production-preset-meta {
  color: #9aa0aa;
  font-size: 12px;
}

.production-preset-ghost {
  opacity: 0.45;
  background: #edf4fd;
}

.production-preset-chosen {
  background: #f7f9fc;
}

.production-preset-list-move {
  transition: transform 0.2s;
}

.prop-input {
  padding: 2px 5px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.form-select {
  cursor: pointer;
  display: block;
  width: 100%;
  padding: 2px 24px 2px 5px;
  font-weight: 400;
  color: #212529;
  background-color: #e9ecef;

  background-image:
    url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");

  background-repeat: no-repeat;
  background-position: right 0.25rem center;
  background-size: 16px 12px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;

  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  appearance: none;
}
</style>
